import React from 'react';
import { MapPin, Award, Heart, Users, Star, Truck, Shield, Phone, Mail } from 'lucide-react';

export default function About() {
  return (
    <div className="min-h-screen py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">About AKIDO™</h1>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            Crafting extraordinary fragrances that tell unique stories and create lasting memories
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
          <div className="space-y-6">
            <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-xl p-8 border border-gold-500/20">
              <h2 className="text-2xl font-bold text-white mb-4">Our Story</h2>
              <p className="text-gray-300 leading-relaxed mb-4">
                AKIDO™ was born from a passion for exceptional fragrances and the belief that every person deserves 
                a signature scent that reflects their unique personality. We curate the finest perfumes from around 
                the world, focusing on quality, authenticity, and the artistry of perfumery.
              </p>
              <p className="text-gray-300 leading-relaxed">
                Our journey began with a simple vision: to make luxury fragrances accessible to everyone while 
                maintaining the highest standards of quality and customer service.
              </p>
            </div>

            <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-xl p-8 border border-gold-500/20">
              <h2 className="text-2xl font-bold text-white mb-4">Our Mission</h2>
              <p className="text-gray-300 leading-relaxed">
                To make luxury fragrances accessible to everyone while supporting artisan perfumers and 
                sustainable practices. We believe in the power of scent to evoke emotions, create memories, 
                and express individuality through carefully curated collections that speak to the soul.
              </p>
            </div>
          </div>

          <div className="space-y-6">
            <div className="bg-gradient-to-br from-gold-900/20 to-gold-800/20 border border-gold-500/20 rounded-xl p-8">
              <div className="flex items-center space-x-3 mb-6">
                <MapPin className="w-6 h-6 text-gold-400" />
                <h2 className="text-2xl font-bold text-white">Founded By</h2>
              </div>
              <div className="space-y-4">
                <div className="text-center">
                  <div className="w-24 h-24 bg-gradient-to-br from-gold-400 to-gold-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-2xl font-bold text-black">M</span>
                  </div>
                  <h3 className="text-2xl font-semibold text-gold-400 mb-2">MOHAMMAD MANNAN</h3>
                  <p className="text-gray-300 mb-2">Founder & Chief Perfumer</p>
                  <div className="flex items-center justify-center space-x-2 text-gray-400 mb-4">
                    <MapPin className="w-4 h-4" />
                    <span>Kashmir, Sopore</span>
                  </div>
                </div>
                <p className="text-gray-300 leading-relaxed text-center">
                  With over a decade of experience in fragrance curation and a deep appreciation for 
                  the art of perfumery, Manan founded AKIDO™ to share his passion for exceptional scents 
                  with fragrance enthusiasts worldwide.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="bg-gray-900 rounded-xl p-6 text-center border border-gold-500/20">
                <Award className="w-8 h-8 text-gold-400 mx-auto mb-3" />
                <h3 className="text-lg font-semibold text-white mb-2">Premium Quality</h3>
                <p className="text-gray-400 text-sm">Authentic fragrances from renowned perfumers</p>
              </div>
              <div className="bg-gray-900 rounded-xl p-6 text-center border border-gold-500/20">
                <Heart className="w-8 h-8 text-gold-400 mx-auto mb-3" />
                <h3 className="text-lg font-semibold text-white mb-2">Passion Driven</h3>
                <p className="text-gray-400 text-sm">Every fragrance carefully selected with love</p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-r from-gray-900 to-black rounded-xl p-8 mb-12 border border-gold-500/20">
          <h2 className="text-3xl font-bold text-white mb-8 text-center">Why Choose AKIDO™?</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="bg-gold-500 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Award className="w-8 h-8 text-black" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">Authentic Products</h3>
              <p className="text-gray-400">100% genuine fragrances directly from certified distributors</p>
            </div>

            <div className="text-center">
              <div className="bg-gold-500 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Truck className="w-8 h-8 text-black" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">Fast Delivery</h3>
              <p className="text-gray-400">Express shipping across India with tracking and insurance</p>
            </div>

            <div className="text-center">
              <div className="bg-gold-500 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="w-8 h-8 text-black" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">Secure Shopping</h3>
              <p className="text-gray-400">Encrypted payments and 7-day return guarantee</p>
            </div>

            <div className="text-center">
              <div className="bg-gold-500 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="w-8 h-8 text-black" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">Expert Support</h3>
              <p className="text-gray-400">24/7 customer service with fragrance consultation</p>
            </div>
          </div>
        </div>

        {/* Customer Reviews */}
        <div className="mb-12">
          <h2 className="text-3xl font-bold text-white mb-8 text-center">What Our Customers Say</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-xl p-6 border border-gold-500/20">
              <div className="flex items-center mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-4 h-4 text-gold-500 fill-current" />
                ))}
              </div>
              <p className="text-gray-300 mb-4">
                "AKIDO™ has the most authentic collection of perfumes. The Midnight Rose Elegance is absolutely divine!"
              </p>
              <p className="text-gold-400 font-semibold">- Priya S., Mumbai</p>
            </div>

            <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-xl p-6 border border-gold-500/20">
              <div className="flex items-center mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-4 h-4 text-gold-500 fill-current" />
                ))}
              </div>
              <p className="text-gray-300 mb-4">
                "Excellent service and fast delivery. The packaging was luxurious and the fragrance quality is top-notch."
              </p>
              <p className="text-gold-400 font-semibold">- Rajesh K., Delhi</p>
            </div>

            <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-xl p-6 border border-gold-500/20">
              <div className="flex items-center mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-4 h-4 text-gold-500 fill-current" />
                ))}
              </div>
              <p className="text-gray-300 mb-4">
                "Found my signature scent at AKIDO™. The chatbot was very helpful in recommending the perfect fragrance!"
              </p>
              <p className="text-gold-400 font-semibold">- Ananya M., Bangalore</p>
            </div>
          </div>
        </div>

        {/* Contact Information */}
        <div className="bg-gradient-to-br from-gold-900/20 to-gold-800/20 border border-gold-500/20 rounded-xl p-8">
          <h2 className="text-3xl font-bold text-white mb-8 text-center">Get In Touch</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-xl font-semibold text-gold-400 mb-4">Contact Information</h3>
              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  <Phone className="w-5 h-5 text-gold-400" />
                  <span className="text-gray-300">+91 98765 43210</span>
                </div>
                <div className="flex items-center space-x-3">
                  <Mail className="w-5 h-5 text-gold-400" />
                  <span className="text-gray-300">support@akido.com</span>
                </div>
                <div className="flex items-center space-x-3">
                  <MapPin className="w-5 h-5 text-gold-400" />
                  <span className="text-gray-300">Kashmir, Sopore, India</span>
                </div>
              </div>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold text-gold-400 mb-4">Business Hours</h3>
              <div className="space-y-2 text-gray-300">
                <p>Monday - Friday: 9:00 AM - 8:00 PM</p>
                <p>Saturday: 10:00 AM - 6:00 PM</p>
                <p>Sunday: 11:00 AM - 5:00 PM</p>
                <p className="text-gold-400">24/7 Online Support Available</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}